<template>
  <ModalModule
    v-if="download"
    v-model="activeModal"
    :title="modalTitle"
    close-icon-text="close"
    :is-full-screen="true"
    :esc-to-close="true"
    :click-to-close="false"
    @abort="emits('closeModal')"
    @after-close="emits('afterCloseModal')"
  >
    <div v-if="!isDownloaded || props.download?.isExternalURL" class="row">
      <div class="d-lg-flex flex-lg-row-reverse">
        <div
          v-if="shareUrl && shareTitle"
          class="col-lg-3 text-lg-end pb-4 col-sm-12"
        >
          <ShareButton
            :url="shareUrl"
            :title="shareTitle"
            :share-text="shareText"
            :copied-text="
              $textDictionary['downloadCenter.copied.label'] ??
              'Copied to Clipboard'
            "
            :copy-text="
              $textDictionary['downloadCenter.copy.label'] ??
              'Copy download link'
            "
            @click="handleOnShareClick"
          />
        </div>
        <div class="col-lg-9 col-sm-12">
          <h2 :class="$style.downloadName">{{ download.name }}</h2>
          <MetaList class="mb-4" :items="metaListItems" />
          <div class="rte-text mb-4" v-html="download.abstract"></div>
          <template v-if="hasMultipleDownloads">
            <div class="form-item">
              <PrimeVueSplitButton
                v-if="(!downloadLocked || !formUrl) && download.mediaLink[language as ContentfulLocaleCode]"
                :model="languageLinks"
                :label="downloadButtonText"
                :button-props="{ class: 'button button--primary' }"
                :menu-button-props="{ class: 'button button--primary' }"
                @click="downloadItem"
              >
                <template #default>
                  <icon name="download" /> {{ downloadButtonText }}
                  <small>
                    {{
                      ' [' +
                      useLanguageLabel(language as ContentfulLocaleCode) +
                      ']'
                    }}
                  </small>
                </template>
                <template #icon>
                  <icon :name="'download'" />
                </template>
                <template #menuitemicon>
                  <icon :name="'download'" />
                </template>
                <template #dropdownicon>
                  <icon :name="'arrow-down'" />
                </template>
                <template #item="itemProps">
                  <div class="p-tieredmenu-item-link">
                    <icon name="download" /> {{ downloadButtonText }}
                    <small>
                      {{ ' [' + itemProps.item.label + ']' }}
                    </small>
                  </div>
                </template>
              </PrimeVueSplitButton>
            </div>
          </template>
          <LinkElement
            v-else-if="(!downloadLocked || !formUrl) && downloadLink"
            :link="downloadLink"
            style-type="primary"
            :target-blank="true"
            class="mt-2 mb-2"
            icon-type="icon-left"
            :icon-name="props.download?.isExternalURL ? 'link' : 'download'"
            download
            @click="
              () => (
                (isDownloaded = true),
                selectDownload(downloadLink),
                trackDownload('single_language', language)
              )
            "
          >
            {{
              $textDictionary['downloadCenter.modal.downloadLabel'] ??
              'Download'
            }}
          </LinkElement>
          <PardotFormIframe
            v-if="downloadLocked && formUrl"
            :id="props.download?.id"
            :form-url="formUrl"
            @success="handleFormSuccess"
          />
        </div>
      </div>
    </div>
    <DownloadsDownloadCenterModalSuccessBlock
      v-else-if="!props.download?.isExternalURL"
      :download-link="selectedDownloadLink"
      :hande-back-to-overview="handleBackToOverview"
    />
  </ModalModule>
  <ModalModule
    v-else
    v-model="activeModal"
    class="rte-text"
    :title="modalTitle"
    close-icon-text="close"
    :esc-to-close="true"
    :click-to-close="false"
    @abort="emits('closeModal')"
    @after-close="emits('afterCloseModal')"
  >
    <LibRichTextRenderer
      :rich-text="$dictionary['downloadCenter.modal.notFound']"
    />
  </ModalModule>
</template>

<script setup lang="ts">
import {
  LinkElement,
  ModalModule,
  ShareButton,
  MetaList,
  Icon
} from '@hypercodestudio/basler-components';
import PardotFormIframe from '~/components/downloads/PardotFormIframe.vue';
import type { DownloadEntryModel } from '~/lib/DownloadService/model';
import type { ContentfulLocaleCode } from '~/lib/ContentfulService/model';
import type { MetaItem } from '@hypercodestudio/basler-components/dist/components/elements/MetaList.vue';
import type { TabsStateInterface } from '@hypercodestudio/basler-components/dist/models/tabs';
import type { HyperlinkInterface } from '@hypercodestudio/basler-components/dist/components/helpers/HyperLink.vue';
import { useDocumentLanguage } from '~/lib/DownloadService/helper/useDocumentLanguage';
import { useLanguageLabel } from '~/stores/downloadStore/useFilterValueLabel';

const {
  $resolvedLocale,
  $dictionary,
  $textDictionary,
  $globalPageSettings,
  $currentEnvName,
  $analytics
} = useNuxtApp();
const logger = useLogger();
const locale = useLocale();
const url = useRequestURL();

interface Props {
  download?: DownloadEntryModel | null;
  open?: boolean;
  preferredLocales?: ContentfulLocaleCode[];
}

interface languageEntry {
  label: string;
  command: () => void;
}

const selectedDownload = ref<string | object | undefined>();
const selectedDownloadLink = computed<HyperlinkInterface>(() => {
  return {
    uri: selectedDownload.value ?? '#',
    target: '_self',
    external: false
  } satisfies HyperlinkInterface;
});

function selectDownload(download: HyperlinkInterface | undefined): void {
  selectedDownload.value = download?.uri ?? '#';
}

// we access the tab state to find out, which tab is currently active
// instead of using a callback on the emit.
const tabState = reactive<TabsStateInterface>({
  tabs: []
});
const activeLanguage = computed<ContentfulLocaleCode | undefined>(
  () =>
    tabState.tabs.find((tab) => tab.isActive)
      ?.computedId as ContentfulLocaleCode
);

const props = defineProps<Props>();

const emits = defineEmits<{
  /**
   * Once the modal was close via the "close" button (is not triggered via "esc").
   */
  closeModal: [];
  /**
   * After the modal was closed (emits always, also while closing via "esc").
   */
  afterCloseModal: [];
}>();

/**
 * Indicated if the download was initiated by the user in the browser.
 */
const isDownloaded = ref(false);

const shareUrl = computed(() =>
  props.download?.id
    ? buildUrlString(
        locale.value,
        `${$globalPageSettings.value?.documentCenterPage?.slug ?? ''}/${
          props.download.id
        }`,
        undefined,
        undefined,
        true
      )
    : undefined
);
const shareTitle = computed(() => props.download?.name);

const modalTitle = computed(() =>
  props.download?.isExternalURL
    ? $textDictionary.value[
        'productDocumentDownloadTab.pageTitle.externalUrl'
      ] ?? 'productDocumentDownloadTab.pageTitle.externalUrl'
    : $textDictionary.value['productDocumentDownloadTab.pageTitle'] ??
      'Document Download'
);

const shareText = computed(() =>
  props.download?.isExternalURL
    ? $textDictionary.value['downloadCenter.shareLink.label'] ??
      'downloadCenter.shareLink.label'
    : $textDictionary.value['downloadCenter.share.label'] ?? 'Share Download'
);

const downloadButtonText = computed(() =>
  props.download?.isExternalURL
    ? $textDictionary.value['downloadCenter.modal.externalLinkLabel'] ??
      'downloadCenter.modal.externalLinkLabel'
    : $textDictionary.value['downloadCenter.modal.downloadLabel'] ?? 'Download'
);

// e.g. 'https://info.baslerweb.com/l/73192/2020-02-24/98pcbf';
const formUrl = computed(() => {
  const urlString = $globalPageSettings.value?.documentsPardotFormUrl;
  if (!urlString) {
    return;
  }

  try {
    const formUrl = new URL(urlString);
    formUrl.searchParams.append('download', 'document');
    formUrl.searchParams.append('domain', $currentEnvName.value);

    const filename = props.download?.name;
    if (filename) {
      formUrl.searchParams.append('filename', filename);
    }

    return formUrl.toString();
  } catch (e) {
    logger.error('could not create new url from pardot url', e);
  }
});

const activeModal = ref(props.open);
const currentLanguage = ref();
const downloadLocked = ref(props.download?.formRequired ?? false);

const hasMultipleDownloads = computed(
  () => props.download && Object.keys(props.download.mediaLink).length > 1
);

const matchingLocale = computed(() => {
  if (!props.download || !props.preferredLocales) {
    return;
  }

  const existingLocales = Object.keys(props.download.mediaLink);

  return props.preferredLocales.find((preferredLocale) =>
    existingLocales.includes(preferredLocale)
  );
});

/**
 * A download link the current active language if language selection is
 * available. No active language is given (which should only the case if
 * only one language is available), the first download link is returned.
 * If no `download` is passed via props or `props.mediaLink` is empty,
 * `undefined` is returned.
 */
const downloadUrl = computed<string | undefined>(() => {
  if (!props.download) {
    return;
  }

  if (activeLanguage.value) {
    // if `undefined` is observed, there must be an error regarding the uids in
    // the tab modul.
    return props.download.mediaLink[activeLanguage.value];
  }

  const downloadLink = Object.values(props.download.mediaLink)[0];
  if (!downloadLink) {
    return;
  }

  return downloadLink;
});

const downloadLink = computed<HyperlinkInterface | undefined>(() => {
  if (!downloadUrl.value) {
    return;
  }

  return {
    uri: downloadUrl.value,
    target: '_blank',
    external: true
  };
});

type InitActiveTabMap = Partial<Record<string | ContentfulLocaleCode, boolean>>;

/**
 * A map which contains the initial active tab for the preferred locale.
 *
 * @see https://gcp.baslerweb.com/jira/browse/DBP-1272
 */
const initialActiveTabMap = computed<InitActiveTabMap>(() => {
  if (!props.download?.mediaLink) {
    return {};
  }

  const languageActiveMap: InitActiveTabMap = Object.fromEntries(
    Object.keys(props.download.mediaLink).map((key) => [key, false])
  );

  if (matchingLocale.value) {
    languageActiveMap[matchingLocale.value] = true;
  } else if (
    $resolvedLocale.value &&
    languageActiveMap[$resolvedLocale.value] != null
  ) {
    languageActiveMap[$resolvedLocale.value] = true;
  } else if (languageActiveMap['en-US'] != null) {
    // general fallback to en-US if available
    languageActiveMap['en-US'] = true;
  } else {
    // otherwise set the first locale active
    const firstLocale = Object.keys(languageActiveMap)[0];
    if (firstLocale) {
      languageActiveMap[firstLocale] = true;
    }
  }

  return languageActiveMap;
});

const language = computed(() => {
  Object.entries(initialActiveTabMap.value).forEach(([key, value]) => {
    if (value === true) {
      currentLanguage.value = key;
    }
  });
  return currentLanguage.value;
});

//DBP-1400
const languageLinks = computed(() => {
  const items: languageEntry[] = [];
  if (props.download?.mediaLink) {
    Object.entries(props.download?.mediaLink).forEach(([key, value]) => {
      if (!initialActiveTabMap.value[key]) {
        items.push({
          label: useLanguageLabel(key as ContentfulLocaleCode),
          command: () => {
            window.open(value, '_blank'),
              (isDownloaded.value = true),
              (selectedDownload.value =
                props.download?.mediaLink[key as ContentfulLocaleCode] ?? '#'),
              trackDownload('multi_language', key);
          }
        });
      }
    });
  }
  return items;
});

const downloadItem = () => (
  window.open(
    props.download?.mediaLink[currentLanguage.value as ContentfulLocaleCode] ??
      '#',
    '_blank'
  ),
  (isDownloaded.value = true),
  (selectedDownload.value =
    props.download?.mediaLink[currentLanguage.value as ContentfulLocaleCode] ??
    '#'),
  trackDownload('multi_language', currentLanguage.value)
);

const metaListItems = computed((): MetaItem[] => {
  const items: MetaItem[] = [];
  if (
    props.download?.dateYear &&
    !isEmpty(props.download?.dateYear) &&
    !props.download?.isExternalURL
  ) {
    items.push({
      text: props.download?.dateYear,
      iconName: 'calendar'
    });
  }

  if (props.download?.fileExtension && props.download.readableFilesize) {
    items.push({
      text:
        props.download?.fileExtension + ', ' + props.download.readableFilesize,
      iconName: 'file'
    });
  }

  if (props.download?.allowedLanguages) {
    items.push({
      iconName: 'globe',
      text: useDocumentLanguage(props.download.allowedLanguages)
    });
  }

  if (props.download?.documentNumber) {
    items.push({
      text: props.download?.documentNumber,
      iconName: 'hashtag'
    });
  }

  return items;
});

function handleOnShareClick() {
  if (props.download) {
    $analytics?.pushToDataLayer({
      event: 'share',
      ecommerce: {
        method: 'Document Center',
        content_type: props.download.downloadCategory,
        item_id: `${props.download.id} | ${props.download.name}`
      }
    });
  }
}

function handleFormSuccess() {
  downloadLocked.value = false;

  $analytics?.pushToDataLayer({
    event: 'formSent',
    goalPage: '/downloadform/sent',
    page_location: url.href
  });

  if (!hasMultipleDownloads.value && downloadUrl.value) {
    forceDownload(downloadUrl.value);
    isDownloaded.value = true;
    trackFileDownload();
  }
}

function trackDownload(action: string, label: string) {
  $analytics.pushToDataLayer({
    action: action,
    category: 'DownloadcenterDocuments',
    event: 'download_document',
    label: label
  });
  trackFileDownload();
}

function trackFileDownload() {
  $analytics.pushToDataLayer({
    action: props.download?.nameEN ?? props.download?.name,
    category: 'download_successful',
    event: 'file_download_c',
    label: props.download?.id ?? 'no_id_found'
  });
}

function handleBackToOverview() {
  isDownloaded.value = false;
}

watch(
  () => props.download?.formRequired,
  () => {
    if (props.download) {
      downloadLocked.value = props.download.formRequired ?? false;
    }
  }
);

watch(
  () => props.open,
  () => {
    activeModal.value = props.open;

    if (!props.open) {
      // reset download state on close
      isDownloaded.value = false;
    }
  }
);
</script>

<style module>
.downloadName {
  hyphens: auto;
}

.tabsComponent :global(.tab-list__item-title) {
  /*
   * This should be moved to the TabPanel component.
   * @see https://gcp.baslerweb.com/jira/browse/DBP-1325
   */
  white-space: nowrap;
}
</style>
